import React from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import Badge from "components/Badge";
import {
  IconUser,
  IconHexagon,
  IconArrowRight,
  IconTrash,
} from "@tabler/icons-react";
import { SnapshotOUT } from "api-client/generated/api";
import { MEStore } from "features/MappingEditor/MappingEditorStore";

interface SnapshotListItemProps {
  snapshot: SnapshotOUT;
  formattedDate: string;
  restoringId: number | null;
  onDelete: (snapshotId: number) => void;
  onRestore: (snapshotId: number) => void;
}

function SnapshotListItem({
  snapshot,
  formattedDate,
  restoringId,
  onDelete,
  onRestore,
}: SnapshotListItemProps) {
  const { t, i18n } = useTranslation();
  const isRestoringThisSnapshot = restoringId === snapshot.id;

  return (
    <div className="py-4 first:pt-2">
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Badge
            type={snapshot.snapshot_type === "FULL" ? "success" : "auto"}
            size="auto"
            className="text-[10px] uppercase font-bold px-2 py-0.5"
          >
            {snapshot.snapshot_type &&
            i18n.exists(`mappingEditor.snapshotType.${snapshot.snapshot_type}`)
              ? t(`mappingEditor.snapshotType.${snapshot.snapshot_type}`)
              : t("mappingEditor.fullSnapshots")}
          </Badge>
          {snapshot.name && snapshot.name !== snapshot.snapshot_type && (
            <span className="text-sm text-gray-400 italic ml-1">
              {snapshot.name}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className="text-xs text-gray-500 text-right leading-5 whitespace-nowrap"
            title={formattedDate}
          >
            {formattedDate}
          </span>
          <button
            onClick={() => onDelete(snapshot.id)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition"
            title={t("mappingEditor.delete")}
          >
            <IconTrash size={18} />
          </button>
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center text-sm text-gray-600">
            <IconUser size={16} className="mr-2 shrink-0" />
            <span className="truncate">
              {snapshot.created_by || t("mappingEditor.automatic")}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <IconHexagon size={16} className="mr-2 shrink-0" />
            <span>
              {snapshot.number_matches} {t("mappingEditor.objects")}
            </span>
          </div>
        </div>
        <span
          title={t("mappingEditor.restoreTooltip")}
          className="inline-block"
        >
          <Button
            type="gray"
            width="fit-content"
            trailingIcon={<IconArrowRight size={16} />}
            onClick={() => onRestore(snapshot.id)}
            loading={isRestoringThisSnapshot}
            disable={MEStore.isUndoInProgress}
            // This ensures the span catches the hover even if the button is disabled
            style={{
              pointerEvents:
                MEStore.isUndoInProgress || isRestoringThisSnapshot
                  ? "none"
                  : "auto",
            }}
          >
            {isRestoringThisSnapshot
              ? t("mappingEditor.restoringSnapshot")
              : t("mappingEditor.restore")}
          </Button>
        </span>
      </div>
    </div>
  );
}

export default SnapshotListItem;
